import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuggestCID, useSuggestExams, useReviewSuggestion } from '@/hooks/ai';
import { useAuth } from '@/contexts/AuthContext';
import { AISuggestionCard } from '@/components/ai';
import { AISuggestion } from '@/api/ai-api';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';

export function ClinicalSuggestionsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [diagnosis, setDiagnosis] = useState('');
  const [symptomsText, setSymptomsText] = useState('');
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([]);
  const [activeTab, setActiveTab] = useState<'cid' | 'exams'>('cid');

  const { mutateAsync: suggestCID, isPending: isCIDLoading } = useSuggestCID();
  const { mutateAsync: suggestExams, isPending: isExamsLoading } = useSuggestExams();
  const { mutateAsync: reviewSuggestion, isPending: isReviewLoading } = useReviewSuggestion();

  const handleSuggest = async () => {
    const symptoms = symptomsText ? symptomsText.split('\n').filter(Boolean) : undefined;
    try {
      if (activeTab === 'cid') {
        const result = await suggestCID({ diagnosis, symptoms });
        setSuggestions(result || []);
      } else {
        const result = await suggestExams({ diagnosis, symptoms });
        setSuggestions(result || []);
      }
    } catch {
      // Error handled by TanStack Query
    }
  };

  const handleReview = async (suggestionId: string, accepted: boolean) => {
    if (!user?.id) return;
    try {
      await reviewSuggestion({ suggestionId, accepted, reviewedBy: user.id });
      setSuggestions(prev =>
        prev.map(s => s.id === suggestionId ? { ...s, accepted } : s)
      );
    } catch {
      // Error handled by TanStack Query
    }
  };

  return (
    <div className="container max-w-4xl py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Sugestões Clínicas IA</h1>
        <p className="text-gray-500 mt-1">Sugestões de CID-10 e exames baseadas em IA</p>
      </div>

      <div className="flex gap-2 mb-4">
        <Button
          variant={activeTab === 'cid' ? 'primary' : 'outline'}
          onClick={() => { setActiveTab('cid'); setSuggestions([]); }}
        >
          Sugerir CID-10
        </Button>
        <Button
          variant={activeTab === 'exams' ? 'primary' : 'outline'}
          onClick={() => { setActiveTab('exams'); setSuggestions([]); }}
        >
          Sugerir Exames
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descrição do Diagnóstico
          </label>
          <Textarea
            value={diagnosis}
            onChange={(e) => setDiagnosis(e.target.value)}
            placeholder="Descreva o diagnóstico do paciente..."
            rows={3}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sintomas (um por linha, opcional)
          </label>
          <Textarea
            value={symptomsText}
            onChange={(e) => setSymptomsText(e.target.value)}
            placeholder="Febre&#10;Tosse&#10;Dor de cabeça"
            rows={3}
          />
        </div>

        <Button
          onClick={handleSuggest}
          disabled={!diagnosis || isCIDLoading || isExamsLoading}
        >
          {isCIDLoading || isExamsLoading ? 'Processando...' : 'Obter Sugestões'}
        </Button>
      </div>

      {suggestions.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-900">
            {activeTab === 'cid' ? 'Códigos CID-10 Sugeridos' : 'Exames Sugeridos'}
          </h2>
          {suggestions.map((s) => (
            <AISuggestionCard
              key={s.id}
              suggestion={s}
              onReview={handleReview}
              isLoading={isReviewLoading}
            />
          ))}
        </div>
      )}

      <div className="mt-8">
        <Button variant="outline" onClick={() => navigate(-1)}>
          Voltar
        </Button>
      </div>
    </div>
  );
}
